import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    formatPartnerMoney,
    getPartnerShopAccess,
    getPartnerStatusLabel,
    sendPartnerShopAccessEstimate,
    sendPartnerShopAccessMessage,
} from '../utils/partnerShopApi';
import { buildPartnerRequestConversationMessages, getPartnerRequestTitle } from '../utils/partnerRequestDisplay';
import PartnerRequestMessages from './PartnerRequestMessages';

const DEFAULT_LINES = [
    { line_type: 'diagnostic', description: '', amount: '', approval_group_label: '' },
    { line_type: 'labor', description: '', amount: '', approval_group_label: '' },
    { line_type: 'parts', description: '', amount: '', approval_group_label: '' },
];
const EMPTY_ESTIMATE_LINE = { line_type: 'other', description: '', amount: '', approval_group_label: '' };

const ESTIMATE_MESSAGE_PLACEHOLDER = 'Estimate note for driver: explain what you will inspect, replace, or confirm before repair.';
const ESTIMATE_LINE_DESCRIPTION_PLACEHOLDERS = {
    diagnostic: 'Diagnostic test or inspection',
    labor: 'Labor operation or install work',
    parts: 'Part or material',
    tax: 'Tax, fees, or disposal',
    other: 'Shop supplies or other charge',
};
const ESTIMATE_LINE_TYPE_LABELS = {
    diagnostic: 'Diagnostic',
    labor: 'Labor',
    parts: 'Parts',
    tax: 'Tax',
    other: 'Other',
};
const PUBLIC_ACCESS_REFRESH_MS = 60000;

function getEstimateLineDescriptionPlaceholder(lineType) {
    return ESTIMATE_LINE_DESCRIPTION_PLACEHOLDERS[lineType] || 'Line item description';
}

function getEstimateLineSubmissionDescription(line) {
    const description = String(line?.description || '').trim();
    if (description) return description;
    return ESTIMATE_LINE_TYPE_LABELS[line?.line_type] || ESTIMATE_LINE_TYPE_LABELS.other;
}

function Icon({ children, className = '' }) {
    return (
        <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
            {children}
        </svg>
    );
}

function getEstimateGroupLabel(line) {
    return String(line?.approval_group_label || '').trim();
}

function getEstimateGroupSectionKey(line) {
    const uiKey = String(line?._approval_group_ui_key || '').trim();
    if (uiKey) return uiKey;
    const label = getEstimateGroupLabel(line);
    return label ? `label:${label.toLowerCase()}` : '';
}

function getEstimateLineSections(lines = []) {
    const groups = [];
    const groupsByKey = new Map();
    const ungrouped = [];

    lines.forEach((line, index) => {
        const key = getEstimateGroupSectionKey(line);
        const item = { line, index };
        if (!key) {
            ungrouped.push(item);
            return;
        }
        if (!groupsByKey.has(key)) {
            const group = { key, label: getEstimateGroupLabel(line), items: [] };
            groupsByKey.set(key, group);
            groups.push(group);
        }
        groupsByKey.get(key).items.push(item);
    });

    return { ungrouped, groups };
}

function getNextEstimateGroupLabel(lines = []) {
    const existing = new Set(lines.map(getEstimateGroupLabel).filter(Boolean).map((label) => label.toLowerCase()));
    let index = 1;
    while (existing.has(`group ${index}`)) index += 1;
    return `Group ${index}`;
}

function EstimateAccessLineRow({ line, onChange, onRemove }) {
    return (
        <div className="grid gap-2 rounded-lg border border-[#d9d9d9] bg-white p-2">
            <select
                value={line.line_type}
                onChange={(event) => onChange({ line_type: event.target.value })}
                className="h-9 rounded-lg border border-[#d9d9d9] bg-white px-2 text-xs font-bold text-[#414141] outline-none focus:border-[#D81F26]"
            >
                <option value="diagnostic">Diagnostic</option>
                <option value="parts">Parts</option>
                <option value="labor">Labor</option>
                <option value="tax">Tax</option>
                <option value="other">Other</option>
            </select>
            <input
                value={line.description}
                onChange={(event) => onChange({ description: event.target.value })}
                placeholder={getEstimateLineDescriptionPlaceholder(line.line_type)}
                className="h-9 rounded-lg border border-[#d9d9d9] bg-white px-2.5 text-sm font-semibold text-[#414141] outline-none placeholder:text-[#9e9e9e] focus:border-[#D81F26]"
            />
            <div className="grid grid-cols-[1fr_38px] gap-2">
                <input
                    type="number"
                    min="0"
                    value={line.amount}
                    onChange={(event) => onChange({ amount: event.target.value })}
                    placeholder="Amount"
                    className="h-9 rounded-lg border border-[#d9d9d9] bg-white px-2.5 text-sm font-semibold text-[#414141] outline-none placeholder:text-[#9e9e9e] focus:border-[#D81F26]"
                />
                <button
                    type="button"
                    onClick={onRemove}
                    className="flex h-9 items-center justify-center rounded-lg text-[#b42318] hover:bg-[#fff1f1]"
                    aria-label="Remove estimate line"
                >
                    <Icon className="h-4 w-4"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Icon>
                </button>
            </div>
        </div>
    );
}

function getVehicleName(request) {
    const vehicle = request?.vehicle || {};
    const parts = [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean);
    return parts.length ? parts.join(' ') : request?.shop_ready_summary?.vehicleName || 'Vehicle';
}

function getRequestTitle(request) {
    return getPartnerRequestTitle(request, 'Diagnostic estimate request');
}

function ListItems({ items = [], emptyText }) {
    const visible = Array.isArray(items) ? items.filter(Boolean).slice(0, 6) : [];
    if (!visible.length) return <p className="text-sm font-semibold text-[#414141]">{emptyText}</p>;
    return (
        <ul className="space-y-1.5">
            {visible.map((item) => (
                <li key={item} className="text-sm font-semibold leading-relaxed text-[#414141]">- {item}</li>
            ))}
        </ul>
    );
}

export default function ShopEstimateAccessPage() {
    const { token } = useParams();
    const [request, setRequest] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [lines, setLines] = useState(() => DEFAULT_LINES.map((line) => ({ ...line })));
    const [isSendingEstimate, setIsSendingEstimate] = useState(false);
    const [estimateNotice, setEstimateNotice] = useState('');

    useEffect(() => {
        let cancelled = false;
        const fetchRequest = (showLoading = false) => {
            if (showLoading) setIsLoading(true);
            getPartnerShopAccess(token)
                .then((nextRequest) => {
                    if (!cancelled) {
                        setRequest(nextRequest);
                        setError('');
                    }
                })
                .catch((err) => {
                    if (!cancelled) setError(err?.message || 'This shop estimate link is unavailable.');
                })
                .finally(() => {
                    if (!cancelled && showLoading) setIsLoading(false);
                });
        };
        fetchRequest(true);
        const intervalId = window.setInterval(() => fetchRequest(false), PUBLIC_ACCESS_REFRESH_MS);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [token]);

    const total = useMemo(
        () => lines.reduce((sum, line) => sum + Number(line.amount || 0), 0),
        [lines]
    );
    const lineSections = useMemo(() => getEstimateLineSections(lines), [lines]);
    const requestMessages = useMemo(
        () => (request ? buildPartnerRequestConversationMessages(request, { driverLabel: 'Driver' }) : []),
        [request]
    );

    function updateLine(index, patch) {
        setLines((prev) => prev.map((line, itemIndex) => (itemIndex === index ? { ...line, ...patch } : line)));
    }

    function removeLine(index) {
        setLines((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    }

    function addStandaloneLine() {
        setLines((prev) => [...prev, { ...EMPTY_ESTIMATE_LINE }]);
    }

    function addGroup() {
        setLines((prev) => {
            const label = getNextEstimateGroupLabel(prev);
            const groupKey = `new:${Date.now()}:${prev.length}`;
            return [...prev, { ...EMPTY_ESTIMATE_LINE, _approval_group_ui_key: groupKey, approval_group_label: label }];
        });
    }

    function addLineToGroup(group) {
        setLines((prev) => [...prev, {
            ...EMPTY_ESTIMATE_LINE,
            _approval_group_ui_key: group.key,
            approval_group_label: group.label,
        }]);
    }

    function renameGroup(groupKey, nextLabel) {
        setLines((prev) => prev.map((line) => (
            getEstimateGroupSectionKey(line) === groupKey
                ? { ...line, _approval_group_ui_key: groupKey, approval_group_label: nextLabel }
                : line
        )));
    }

    async function handleShopMessage(nextMessage) {
        const updated = await sendPartnerShopAccessMessage(token, nextMessage);
        if (updated) setRequest(updated);
    }

    async function handleSendEstimate() {
        if (lines.some((line) => getEstimateGroupSectionKey(line) && !getEstimateGroupLabel(line))) {
            setEstimateNotice('Name each estimate group before sending.');
            return;
        }

        const cleanedLines = lines
            .map((line) => {
                const rawAmount = String(line.amount ?? '').trim();

                return {
                    description: getEstimateLineSubmissionDescription(line),
                    amount: rawAmount === '' ? NaN : Number(rawAmount),
                    line_type: line.line_type || 'other',
                    approval_group_label: String(line.approval_group_label || '').trim() || null,
                };
            })
            .filter((line) => Number.isFinite(line.amount) && line.amount >= 0);
        if (!cleanedLines.length) {
            setEstimateNotice('Add at least one estimate amount.');
            return;
        }

        setIsSendingEstimate(true);
        setEstimateNotice('');
        try {
            const updated = await sendPartnerShopAccessEstimate(token, {
                message,
                currency: 'USD',
                lines: cleanedLines,
            });
            if (updated) setRequest(updated);
            setEstimateNotice('Estimate sent to CarFido.');
        } catch (err) {
            setEstimateNotice(err?.message || 'Could not send estimate.');
        } finally {
            setIsSendingEstimate(false);
        }
    }

    if (isLoading) {
        return (
            <main className="flex min-h-screen items-center justify-center bg-[#F9F9F9]">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#d9d9d9] border-t-[#D81F26]" />
            </main>
        );
    }

    if (error || !request) {
        return (
            <main className="flex min-h-screen items-center justify-center bg-[#F9F9F9] p-6 text-center">
                <section className="max-w-sm rounded-2xl border border-[#d9d9d9] bg-white p-6 shadow-sm">
                    <p className="text-xl font-black text-[#414141]">Estimate link unavailable</p>
                    <p className="mt-2 text-sm font-semibold leading-relaxed text-[#414141]">{error || 'This request could not be loaded.'}</p>
                </section>
            </main>
        );
    }

    const summary = request.shop_ready_summary || {};
    const latestEstimate = request.latest_estimate;

    return (
        <main className="min-h-screen bg-[#F9F9F9] text-[#414141]">
            <div className="mx-auto grid w-full max-w-6xl gap-5 px-4 py-6 md:grid-cols-[minmax(0,1fr)_380px] md:px-6 md:py-8">
                <section className="space-y-4">
                    <header className="rounded-2xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded-full border border-[#d8d8d8] bg-[#f6f6f6] px-3 py-1 text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">
                                {getPartnerStatusLabel(request.request_status, 'shop')}
                            </span>
                            <span className="text-xs font-bold text-[#414141]">CarFido relay request</span>
                        </div>
                        <h1 className="mt-3 text-2xl font-black leading-tight text-[#414141]">{getRequestTitle(request)}</h1>
                        <p className="mt-2 text-sm font-semibold text-[#414141]">{getVehicleName(request)}</p>
                    </header>

                    <section className="rounded-2xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
                        <div className="flex items-start gap-3">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F2F2F2] text-[#414141]">
                                <Icon><path d="M9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></Icon>
                            </span>
                            <div>
                                <p className="text-base font-black text-[#414141]">Shop-ready diagnostic summary</p>
                                <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">
                                    {summary.customerComplaint || request.services?.[0]?.summary || 'Review the request and send CarFido an estimate or message.'}
                                </p>
                            </div>
                        </div>
                        <div className="mt-5 grid gap-4 sm:grid-cols-2">
                            <div>
                                <p className="mb-2 text-xs font-black uppercase tracking-[0.08em] text-[#414141]">Likely causes</p>
                                <ListItems items={summary.likelyCauses || []} emptyText="Pending shop review." />
                            </div>
                            <div>
                                <p className="mb-2 text-xs font-black uppercase tracking-[0.08em] text-[#414141]">Requested tests</p>
                                <ListItems items={summary.requestedTests || []} emptyText="Inspect and confirm root cause." />
                            </div>
                        </div>
                    </section>

                    <PartnerRequestMessages
                        request={request}
                        messages={requestMessages}
                        currentRole="shop"
                        title="Message driver through CarFido"
                        emptyText="No messages yet."
                        placeholder="Reply through CarFido..."
                        onSend={handleShopMessage}
                    />
                </section>

                <aside className="space-y-4">
                    <section className="rounded-2xl border border-[#d9d9d9] bg-white p-4 shadow-sm">
                        <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">Itemized estimate</p>
                        {latestEstimate && (
                            <div className="mt-3 rounded-xl border border-[#dfd0ff] bg-[#f7f2ff] px-3 py-2">
                                <p className="text-xs font-black text-[#6f3cc3]">Latest estimate sent</p>
                                <p className="mt-1 text-xl font-black text-[#414141]">{formatPartnerMoney(latestEstimate.total_amount, latestEstimate.currency)}</p>
                            </div>
                        )}
                        <textarea
                            value={message}
                            onChange={(event) => setMessage(event.target.value)}
                            placeholder={ESTIMATE_MESSAGE_PLACEHOLDER}
                            rows={4}
                            className="mt-3 w-full resize-y rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 py-2 text-sm font-semibold text-[#414141] outline-none placeholder:text-[#9e9e9e] focus:border-[#D81F26]"
                        />
                        <div className="mt-3 space-y-2">
                            {lineSections.ungrouped.length > 0 && (
                                <div className="rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] p-2">
                                    <div className="mb-2 flex items-center justify-between gap-2 px-1">
                                        <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Individual line items</p>
                                        <span className="text-[11px] font-bold text-[#9e9e9e]">Approved one at a time</span>
                                    </div>
                                    <div className="space-y-2">
                                        {lineSections.ungrouped.map(({ line, index }) => (
                                            <EstimateAccessLineRow
                                                key={index}
                                                line={line}
                                                onChange={(patch) => updateLine(index, patch)}
                                                onRemove={() => removeLine(index)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {lineSections.groups.map((group) => (
                                <div key={group.key} className="rounded-xl border border-[#d8d8d8] bg-[#f5f5f5] p-2">
                                    <div className="mb-2 space-y-2">
                                        <input
                                            value={group.label}
                                            onChange={(event) => renameGroup(group.key, event.target.value)}
                                            placeholder="Group name"
                                            aria-label="Approval group name"
                                            className="h-9 w-full rounded-lg border border-[#d8d8d8] bg-white px-2.5 text-sm font-black text-[#414141] outline-none placeholder:text-[#9e9e9e] focus:border-[#D81F26]"
                                        />
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="rounded-full border border-[#d8d8d8] bg-white px-2 py-1 text-[10px] font-black text-[#6B6B6B]">
                                                Grouped approval
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => addLineToGroup(group)}
                                                className="rounded-lg border border-[#d8d8d8] bg-white px-2.5 py-1.5 text-xs font-black text-[#414141] hover:bg-[#F2F2F2]"
                                            >
                                                Add item
                                            </button>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        {group.items.map(({ line, index }) => (
                                            <EstimateAccessLineRow
                                                key={index}
                                                line={line}
                                                onChange={(patch) => updateLine(index, patch)}
                                                onRemove={() => removeLine(index)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 flex items-center justify-between gap-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    onClick={addStandaloneLine}
                                    className="rounded-xl border border-[#d9d9d9] bg-white px-3 py-2 text-xs font-black text-[#414141] hover:bg-[#F2F2F2]"
                                >
                                    Add line
                                </button>
                                <button
                                    type="button"
                                    onClick={addGroup}
                                    className="rounded-xl border border-[#d8d8d8] bg-[#f5f5f5] px-3 py-2 text-xs font-black text-[#414141] hover:bg-[#eeeeee]"
                                >
                                    Add group
                                </button>
                            </div>
                            <p className="text-sm font-black text-[#414141]">Total {formatPartnerMoney(total)}</p>
                        </div>
                        {estimateNotice && (
                            <p className={`mt-3 rounded-xl px-3 py-2 text-sm font-bold ${/could not|add at least/i.test(estimateNotice) ? 'border border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]' : 'border border-[#bde7d5] bg-[#ecfff5] text-[#087a43]'}`}>
                                {estimateNotice}
                            </p>
                        )}
                        <button
                            type="button"
                            onClick={handleSendEstimate}
                            disabled={isSendingEstimate}
                            className="mt-4 inline-flex h-11 w-full items-center justify-center rounded-xl bg-[#D81F26] px-5 text-sm font-black text-white hover:bg-[#B8181E] disabled:cursor-wait disabled:opacity-60"
                        >
                            {isSendingEstimate ? 'Sending estimate...' : latestEstimate ? 'Resend estimate' : 'Send estimate'}
                        </button>
                    </section>
                </aside>
            </div>
        </main>
    );
}
